import * as React from "react";
import { useCallback, useState, useMemo } from "react";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useSendTelemetryOnMount from "logic/telemetry/useSendTelemetryOnMount";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { useEnableLatestPacks, useDisableLatestPacks, useGetPacksSummary } from "illuminate/hooks/usePacksAPI";
import { useModalContext, useSelectedRows } from "common/contexts";
import { ConfirmDialog } from "common/components";
import EntityCreateShareFormGroup from "components/permissions/EntityCreateShareFormGroup";
import { CurrentUserStore } from "stores/users/CurrentUserStore";
import PacksList from "./packs-list";
import { buildEnableDisablePayload } from "../../utils";
const CONST_MAP = {
  enable: {
    data: "creation",
    title: "Enable"
  },
  disable: {
    data: "loss",
    title: "Disable"
  }
};
function ConfEnableDisablePacks({ onCancel }) {
  const { entity: actionType } = useModalContext();
  const selectedPacks = useSelectedRows();
  const { enablingPacks, enablePacks } = useEnableLatestPacks();
  const { disablingPacks, disablePacks } = useDisableLatestPacks();
  const [shareRequest, setShareRequest] = useState(void 0);
  const sendTelemetry = useSendTelemetry("packs");
  const {
    packsSummary: { cached_version, current_version }
  } = useGetPacksSummary();
  const updatingPacks = useMemo(() => enablingPacks || disablingPacks, [enablingPacks, disablingPacks]);
  useSendTelemetryOnMount(
    sendTelemetry,
    TELEMETRY_EVENT_TYPE.ILLUMINATE[`PACK_${actionType.toUpperCase()}_MODAL_TOGGLED`],
    {
      app_pathname: "illuminate",
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: ""
      }
    },
    [actionType]
  );
  const handleEnablingPacks = useCallback(async () => {
    const packsToEnable = selectedPacks.filter((pack) => !pack.enabled);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE.PACKS_BULK_UPDATED, {
      app_pathname: "illuminate",
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: cached_version || current_version,
        illuminate_action: actionType,
        illuminate_packs: packsToEnable.map((pack) => ({
          id: pack.pack_id,
          title: pack.title,
          version: pack.version
        }))
      }
    });
    const entity = buildEnableDisablePayload(packsToEnable);
    return enablePacks({ requestObject: { entity, share_request: shareRequest } });
  }, [actionType, cached_version, current_version, enablePacks, selectedPacks, sendTelemetry, shareRequest]);
  const handleDisablingPacks = useCallback(async () => {
    const packsToDisable = selectedPacks.filter((pack) => pack.enabled);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE.PACKS_BULK_UPDATED, {
      app_pathname: "illuminate",
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: current_version,
        illuminate_action: actionType,
        illuminate_packs: packsToDisable.map((pack) => ({
          id: pack.pack_id,
          title: pack.title,
          version: pack.version
        }))
      }
    });
    const requestObject = buildEnableDisablePayload(packsToDisable);
    return disablePacks({ requestObject });
  }, [actionType, current_version, disablePacks, selectedPacks, sendTelemetry]);
  const onConfirm = useCallback(async () => {
    if (actionType === "enable") await handleEnablingPacks().then(() => CurrentUserStore.reload());
    if (actionType === "disable") await handleDisablingPacks().then(() => CurrentUserStore.reload());
    onCancel();
  }, [actionType, handleDisablingPacks, handleEnablingPacks, onCancel]);
  const combinedAction = actionType + (actionType === "enable" && selectedPacks.filter((pack) => pack.update_available).length > 0 ? " or update" : "");
  return /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: true,
      onConfirm,
      confirmDisabled: updatingPacks,
      isLoading: updatingPacks,
      confirmText: "Confirm",
      onCancel,
      title: `${CONST_MAP[actionType].title} Illuminate Pack${selectedPacks.length > 1 ? "s" : ""}`
    },
    /* @__PURE__ */ React.createElement("p", null, "You are about to ", /* @__PURE__ */ React.createElement("b", null, combinedAction), " the packs listed below"),
    /* @__PURE__ */ React.createElement("p", null, "This could cause potential ", CONST_MAP[actionType].data, " of data and will pause all processing for a few seconds."),
    /* @__PURE__ */ React.createElement("p", null, "Do you wish to continue?"),
    /* @__PURE__ */ React.createElement(PacksList, null),
    actionType === "enable" && /* @__PURE__ */ React.createElement(
      EntityCreateShareFormGroup,
      {
        entityType: "Illuminate pack",
        description: "Search for a User or Team to share the content of these Illuminate packs with:",
        entityTitle: "",
        onSetEntityShare: setShareRequest
      }
    )
  );
}
export default ConfEnableDisablePacks;
